import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Paper,
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  CircularProgress,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Delete as DeleteIcon,
  AccountBalance as AccountBalanceIcon,
  MoneyOff as MoneyOffIcon,
  Payments as PaymentsIcon,
  ContentCopy as ContentCopyIcon,
  FilterList as FilterListIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';
import { getPaymentHistory, getPaymentByReference } from '../services/paymentService';
import {
  DATE_FILTER_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_STATUS_COLORS,
} from '../constants/PaymentConstants';
import { getDateRangeFromFilter, formatDateTime, formatCurrency } from '../utils/paymentUtils';

// Delete is intentionally disabled — this guard prevents enabling it via browser devtools
const DELETE_ACTION_ALLOWED = false;

const PaymentPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const deleteGuardRef = useRef(DELETE_ACTION_ALLOWED);

  const {
    page,
    pageSize: rowsPerPage,
    handlePageChange,
    handlePageSizeChange,
  } = usePaginationWithUrl({ defaultPageSize: 10 });

  const initialDateRange = getDateRangeFromFilter('today');

  const [dateFilter, setDateFilter] = useState('today');
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [sourceType, setSourceType] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentReference, setPaymentReference] = useState('');

  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const referenceDebounceRef = useRef(null);

  // When reference field has a value, all other filters are locked
  const isReferenceSearchMode = paymentReference.trim() !== '';

  const hasActiveFilters = dateFilter !== 'today' || sourceType !== '' || paymentMethod !== '' || paymentReference !== '';

  useEffect(() => {
    if (startDate && endDate) loadPaymentData();
    // eslint-disable-next-line
  }, [page, rowsPerPage]);

  const handleCopy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(t('copied_to_clipboard') || 'Copied to clipboard');
    }).catch(() => {
      toast.error(t('copy_failed') || 'Failed to copy');
    });
  };

  const loadPaymentData = async (
    start = startDate,
    end = endDate,
    source = sourceType,
    method = paymentMethod,
    reference = paymentReference
  ) => {
    if (!start || !end) {
      toast.warning(t('please_select_date_range') || 'Please select a date range');
      return;
    }
    setLoading(true);
    try {
      const response = await getPaymentHistory({
        startDate: start,
        endDate: end,
        sourceType: source,
        paymentMethod: method,
        paymentReference: reference || undefined,
        PageNumber: page + 1,
        PageSize: rowsPerPage,
        SortBy: 'PaymentDate',
        SortDirection: 'desc',
      });

      const payload = response?.data?.payload || response?.payload;
      const paymentsData = payload?.Payments;
      const summaryData = payload?.Summary;

      setPayments(Array.isArray(paymentsData?.Items) ? paymentsData.Items : []);
      setTotalCount(paymentsData?.TotalItems || 0);
      setSummary(summaryData || null);
    } catch {
      toast.error(t('failed_to_load_payment_data') || 'Failed to load payment data');
      setPayments([]);
      setTotalCount(0);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handleDateFilterChange = (filter) => {
    setDateFilter(filter);
    if (filter !== 'custom') {
      const range = getDateRangeFromFilter(filter);
      setStartDate(range.startDate);
      setEndDate(range.endDate);
      // Auto-search when quick filter is clicked
      loadPaymentData(range.startDate, range.endDate, sourceType, paymentMethod, paymentReference);
    }
  };

  const handleSearch = () => loadPaymentData();

  const handleReferenceSearch = async (reference) => {
    setLoading(true);
    try {
      const response = await getPaymentByReference(reference);
      const payload = response?.data?.payload || response?.payload;
      if (payload && payload.Id) {
        setPayments([{
          Id: payload.Id,
          PaymentReference: payload.PaymentReference,
          SourceType: payload.SourceType,
          SourceId: payload.SourceId,
          SourceDocumentNumber: payload.SourceDocumentNumber,
          PaymentDate: payload.PaymentDate,
          PaymentMethod: payload.PaymentMethod,
          PaymentMethodId: payload.PaymentMethodId,
          CustomerName: payload.CustomerName,
          RecordedByUserName: payload.RecordedByUserName,
          TotalDocumentAmount: payload.TotalDocumentAmount,
          TotalPaidAmount: payload.TotalPaidAmount,
          RemainingBalance: payload.RemainingBalance,
          PaymentStatus: payload.PaymentStatus,
          IsFullPayment: payload.IsFullPayment,
          IsPartialPayment: payload.IsPartialPayment,
          InvoiceId: payload.SourceType === 'Invoice' ? payload.SourceId : null,
        }]);
        setTotalCount(1);
      } else {
        setPayments([]);
        setTotalCount(0);
      }
      setSummary(null);
    } catch (err) {
      if (err.response?.status === 404) {
        setPayments([]);
        setTotalCount(0);
        toast.info(t('payment_reference_not_found') || 'No payment found for this reference');
      } else {
        toast.error(t('failed_to_load_payment_data') || 'Failed to load payment data');
      }
      setSummary(null);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentReferenceChange = (rawValue) => {
    const value = rawValue.replace(/\s/g, ''); // strip whitespace
    setPaymentReference(value);
    if (referenceDebounceRef.current) clearTimeout(referenceDebounceRef.current);
    if (value.trim() === '') {
      // Reference cleared — reload normal paginated data
      referenceDebounceRef.current = setTimeout(() => loadPaymentData(), 300);
      return;
    }
    referenceDebounceRef.current = setTimeout(() => handleReferenceSearch(value.trim()), 1500);
  };

  const handleClearReference = () => handlePaymentReferenceChange('');

  const handleClearFilters = () => {
    const range = getDateRangeFromFilter('today');
    setDateFilter('today');
    setStartDate(range.startDate);
    setEndDate(range.endDate);
    setSourceType('');
    setPaymentMethod('');
    setPaymentReference('');
    if (referenceDebounceRef.current) clearTimeout(referenceDebounceRef.current);
    loadPaymentData(range.startDate, range.endDate, '', '', '');
  };

  const handleViewDetails = (payment) => {
    if (payment.SourceType === 'Invoice' && payment.InvoiceId) {
      navigate(`/invoices/view/${payment.InvoiceId}`);
    } else if (payment.SourceType === 'Ticket' && payment.SourceId) {
      navigate(`/tickets/${payment.SourceId}`);
    }
  };

  const handleDeletePayment = (payment) => {
    // Protected: even if the disabled attribute is removed via browser devtools, this guard prevents execution
    if (!deleteGuardRef.current) return;
    toast.error('Delete is not permitted');
  };

  const getSourceTypeChip = (src) => {
    const colors = { Invoice: 'primary', Offer: 'secondary', Ticket: 'info' };
    return (
      <Chip label={src} color={colors[src] || 'default'} size="small" sx={{ fontWeight: 500, fontSize: '0.75rem', height: 24 }} />
    );
  };

  const getPaymentStatusChip = (status) => (
    <Chip label={status} color={PAYMENT_STATUS_COLORS[status] || 'default'} size="small" sx={{ fontWeight: 600, fontSize: '0.75rem', height: 24 }} />
  );

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ReceiptIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'primary.main' }}>
              {t('payment_history') || 'Payment History'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('payment_history_desc') || 'View and manage all payment transactions'}
            </Typography>
          </Box>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={handleSearch} disabled={loading} sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 500 }}>
          {t('refresh') || 'Refresh'}
        </Button>
      </Box>

      {/* Row 1 — Summary totals */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderTop: '3px solid #4caf50' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>{t('total_document_amount') || 'Total Document Amount'}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'success.main' }}>
                    {loading ? <CircularProgress size={20} /> : formatCurrency(summary?.TotalDocumentAmount ?? 0)}
                  </Typography>
                </Box>
                <AccountBalanceIcon sx={{ fontSize: 40, color: 'success.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderTop: '3px solid #2196f3' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>{t('total_paid_amount') || 'Total Paid Amount'}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {loading ? <CircularProgress size={20} /> : formatCurrency(summary?.TotalPaidAmount ?? 0)}
                  </Typography>
                </Box>
                <AttachMoneyIcon sx={{ fontSize: 40, color: 'primary.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ borderRadius: 2, boxShadow: 2, borderTop: '3px solid #ff9800' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>{t('total_remaining_balance') || 'Total Remaining Balance'}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'warning.main' }}>
                    {loading ? <CircularProgress size={20} /> : formatCurrency(summary?.TotalRemainingBalance ?? 0)}
                  </Typography>
                </Box>
                <MoneyOffIcon sx={{ fontSize: 40, color: 'warning.light', opacity: 0.5 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Row 2 — By Payment Method cards */}
      {summary?.ByPaymentMethod?.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          {summary.ByPaymentMethod.map((pm) => (
            <Grid item xs={12} sm={6} md={3} key={pm.PaymentMethod}>
              <Card sx={{ borderRadius: 2, boxShadow: 1, border: '1px solid', borderColor: 'divider' }}>
                <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PaymentsIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      {pm.PaymentMethod}
                    </Typography>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>{formatCurrency(pm.TotalAmount)}</Typography>
                  <Typography variant="caption" color="text.secondary">{pm.PaymentCount} {t('transactions') || 'transactions'}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        {/* Quick date filters */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          {DATE_FILTER_OPTIONS.filter((o) => o.value !== 'custom').map((option) => (
            <Chip
              key={option.value}
              label={option.label}
              size="small"
              clickable
              onClick={() => handleDateFilterChange(option.value)}
              color={dateFilter === option.value ? 'primary' : 'default'}
              variant={dateFilter === option.value ? 'filled' : 'outlined'}
              disabled={isReferenceSearchMode}
              sx={{ fontWeight: dateFilter === option.value ? 600 : 400 }}
            />
          ))}
          {hasActiveFilters && (
            <Chip
              icon={<FilterListIcon sx={{ fontSize: '14px !important' }} />}
              label={t('filters_applied') || 'Filters applied'}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ ml: 'auto', fontWeight: 500 }}
            />
          )}
        </Box>

        {/* Filter fields — single row */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Box sx={{ flex: '2 1 180px', minWidth: 160 }}>
            <TextField
              fullWidth
              size="small"
              label={t('search_by_reference') || 'Search by Reference'}
              value={paymentReference}
              onChange={(e) => handlePaymentReferenceChange(e.target.value)}
              onPaste={(e) => {
                e.preventDefault();
                const pasted = e.clipboardData.getData('text').replace(/\s/g, '');
                handlePaymentReferenceChange(pasted);
              }}
              placeholder={t('search_reference_or_doc') || 'Payment reference / Document number'}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 16, color: 'text.disabled' }} /></InputAdornment>,
                endAdornment: paymentReference ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={handleClearReference} edge="end" sx={{ p: 0.25 }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
              FormHelperTextProps={{ sx: { minHeight: '20px', mb: 0 } }}
              helperText={isReferenceSearchMode ? (t('other_filters_disabled') || 'Other filters disabled while searching by reference') : ' '}
            />
          </Box>
          <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('start_date') || 'Start Date'}
              value={startDate || ''}
              onChange={(e) => { setStartDate(e.target.value); setDateFilter('custom'); }}
              InputLabelProps={{ shrink: true }}
              disabled={isReferenceSearchMode}
              FormHelperTextProps={{ sx: { minHeight: '20px', mb: 0 } }}
              helperText=" "
            />
          </Box>
          <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label={t('end_date') || 'End Date'}
              value={endDate || ''}
              onChange={(e) => { setEndDate(e.target.value); setDateFilter('custom'); }}
              InputLabelProps={{ shrink: true }}
              disabled={isReferenceSearchMode}
              FormHelperTextProps={{ sx: { minHeight: '20px', mb: 0 } }}
              helperText=" "
            />
          </Box>
          <Box sx={{ flex: '1 1 130px', minWidth: 120 }}>
            <FormControl fullWidth size="small" disabled={isReferenceSearchMode}>
              <InputLabel shrink>{t('source_type') || 'Source Type'}</InputLabel>
              <Select value={sourceType} onChange={(e) => setSourceType(e.target.value)} label={t('source_type') || 'Source Type'} displayEmpty notched>
                {SOURCE_TYPE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label || t('all_source_types') || 'All Source Types'}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ minHeight: '20px', fontSize: '0.75rem', color: 'transparent' }}> </Box>
          </Box>
          <Box sx={{ flex: '1 1 140px', minWidth: 130 }}>
            <FormControl fullWidth size="small" disabled={isReferenceSearchMode}>
              <InputLabel shrink>{t('payment_method') || 'Payment Method'}</InputLabel>
              <Select value={paymentMethod} onChange={(e) => setPaymentMethod(e.target.value)} label={t('payment_method') || 'Payment Method'} displayEmpty notched>
                {PAYMENT_METHOD_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label || t('all_methods') || 'All Methods'}</MenuItem>)}
              </Select>
            </FormControl>
            <Box sx={{ minHeight: '20px', fontSize: '0.75rem', color: 'transparent' }}> </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, flex: '0 0 auto', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch} disabled={loading || isReferenceSearchMode} sx={{ borderRadius: 1, textTransform: 'none', fontWeight: 500, height: 40, whiteSpace: 'nowrap' }}>
                {t('search') || 'Search'}
              </Button>
              <Tooltip title={t('clear_filters') || 'Clear Filters'}>
                <IconButton color="secondary" onClick={handleClearFilters} disabled={loading} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, height: 40, width: 40 }}>
                  <ClearIcon />
                </IconButton>
              </Tooltip>
            </Box>
            <Box sx={{ minHeight: '20px', fontSize: '0.75rem', color: 'transparent' }}> </Box>
          </Box>
        </Box>
      </Paper>

      {/* Payments Table */}
      <Paper sx={{ borderRadius: 2, boxShadow: 2 }}>
        <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>{t('payment_transactions') || 'Payment Transactions'}</Typography>
            <Typography variant="body2" color="text.secondary">
              {totalCount} {t('payments_found') || 'payments found'} · {t('page') || 'Page'} {page + 1}
            </Typography>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ bgcolor: 'grey.50' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>{t('reference') || 'Reference'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('date') || 'Date'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('customer') || 'Customer'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('document') || 'Document'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('source_type') || 'Source'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('method') || 'Method'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('doc_amount') || 'Doc. Amount'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('paid_amount') || 'Paid'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">{t('remaining') || 'Remaining'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('recorded_by') || 'Recorded By'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{t('status') || 'Status'}</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="center">{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {payments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                      <Typography variant="body1" color="text.secondary">
                        {t('no_payments_found') || 'No payments found for the selected filters'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  payments.map((payment) => (
                    <TableRow key={payment.Id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 500, fontSize: '0.8rem' }}>
                            {payment.PaymentReference}
                          </Typography>
                          <Tooltip title={t('copy') || 'Copy'}>
                            <IconButton size="small" onClick={() => handleCopy(payment.PaymentReference)} sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {formatDateTime(payment.PaymentDate)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>{payment.CustomerName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {payment.SourceDocumentNumber}
                          </Typography>
                          <Tooltip title={t('copy') || 'Copy'}>
                            <IconButton size="small" onClick={() => handleCopy(payment.SourceDocumentNumber)} sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
                              <ContentCopyIcon sx={{ fontSize: 13 }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>{getSourceTypeChip(payment.SourceType)}</TableCell>
                      <TableCell>
                        <Chip label={payment.PaymentMethod} size="small" variant="outlined" sx={{ fontWeight: 500, fontSize: '0.75rem', height: 24 }} />
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(payment.TotalDocumentAmount)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>{formatCurrency(payment.TotalPaidAmount)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" sx={{ fontWeight: 600, color: payment.RemainingBalance > 0 ? 'warning.main' : 'text.secondary' }}>
                          {formatCurrency(payment.RemainingBalance)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{payment.RecordedByUserName || '—'}</Typography>
                      </TableCell>
                      <TableCell>{getPaymentStatusChip(payment.PaymentStatus)}</TableCell>
                      <TableCell align="center">
                        <Tooltip title={t('view_details') || 'View Details'}>
                          <IconButton size="small" onClick={() => handleViewDetails(payment)} sx={{ color: 'primary.main' }}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('delete_not_permitted') || 'Delete not permitted'}>
                          {/* Wrapper span allows Tooltip on a disabled button */}
                          <span>
                            <IconButton
                              size="small"
                              disabled
                              onClick={() => handleDeletePayment(payment)}
                              sx={{ color: 'error.light', opacity: 0.4, pointerEvents: 'none' }}
                              aria-disabled="true"
                              tabIndex={-1}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {!loading && payments.length > 0 && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            count={totalCount}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handlePageSizeChange}
            sx={{ borderTop: '1px solid', borderColor: 'divider' }}
          />
        )}
      </Paper>
    </Container>
  );
};

export default PaymentPage;
